import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { format, parseISO, differenceInHours } from 'date-fns';
import { useLanguage } from '../contexts/LanguageContext';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, ShoppingCart, Trash2, CreditCard, LayoutGrid, Car, Clock, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CartPage() {
  const { t, dateLocale } = useLanguage();
  const navigate = useNavigate();
  const [cart, setCart] = useState({ items: [], total: 0, item_count: 0 });
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(false);

  const fetchCart = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/cart`, { withCredentials: true });
      setCart(data);
    } catch {
      toast.error('Error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCart(); }, [fetchCart]);

  const handleRemove = async (itemId) => {
    try {
      await axios.delete(`${API_URL}/api/cart/${itemId}`, { withCredentials: true });
      toast.success(t('removeFromCart'));
      await fetchCart();
    } catch {
      toast.error('Error');
    }
  };

  const handleCheckout = async () => {
    setCheckingOut(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/cart/checkout`, {}, { withCredentials: true });
      toast.success(t('paymentConfirmed'), {
        description: data.confirmation_email_sent ? t('confirmationEmailSent') : undefined
      });
      setCart({ items: [], total: 0, item_count: 0 });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setCheckingOut(false);
    }
  };

  const getExpiryLabel = (expiresAt) => {
    const hours = Math.max(0, differenceInHours(parseISO(expiresAt), new Date()));
    return `${hours}h`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-100px)]">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="cart-page">
      <Header />
      <main className="max-w-3xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-extrabold text-slate-900 flex items-center gap-2 mb-6">
          <ShoppingCart className="w-6 h-6" /> {t('cart')}
        </h1>

        {cart.items.length === 0 ? (
          <Card className="card-floating">
            <CardContent className="py-16 text-center">
              <ShoppingCart className="w-16 h-16 mx-auto text-slate-200 mb-4" />
              <p className="text-lg font-bold text-slate-500">{t('cartEmpty')}</p>
              <p className="text-sm text-slate-400 mt-1">{t('cartEmptyDesc')}</p>
              <div className="flex gap-3 justify-center mt-6">
                <Button onClick={() => navigate('/dashboard')} className="btn-primary">
                  <LayoutGrid className="w-4 h-4 mr-2" /> {t('desks')}
                </Button>
                <Button onClick={() => navigate('/parking')} variant="outline" className="rounded-full">
                  <Car className="w-4 h-4 mr-2" /> {t('parking')}
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {/* Warning */}
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3 text-center">
              <p className="text-sm text-amber-700 font-medium flex items-center justify-center gap-1">
                <Clock className="w-4 h-4" /> {t('cartExpiry')}
              </p>
            </div>

            {/* Items */}
            {cart.items.map((item) => (
              <Card key={item.id} className="card-floating" data-testid={`cart-item-${item.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                        item.item_type === 'desk' ? 'bg-blue-100 text-blue-600' : 'bg-emerald-100 text-emerald-600'
                      }`}>
                        {item.item_type === 'desk' ? <LayoutGrid className="w-5 h-5" /> : <Car className="w-5 h-5" />}
                      </div>
                      <div>
                        <p className="font-bold text-slate-900">{item.resource_label}</p>
                        <p className="text-xs text-slate-500">
                          {item.dates.map(d => format(parseISO(d), 'dd/MM', { locale: dateLocale })).join(', ')}
                          {item.period && ` (${item.period})`}
                        </p>
                        <p className="text-[10px] text-amber-600 flex items-center gap-1 mt-0.5">
                          <Clock className="w-3 h-3" /> {t('expiresIn')} {getExpiryLabel(item.expires_at)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <p className="text-lg font-extrabold text-slate-900">
                        CHF {item.price.toFixed(2)}
                      </p>
                      <Button variant="ghost" size="icon" onClick={() => handleRemove(item.id)}
                        data-testid={`remove-cart-${item.id}`}
                        className="text-rose-400 hover:text-rose-600 hover:bg-rose-50 rounded-full">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {/* Total + Actions */}
            <Card className="card-floating border-2 border-blue-200">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-slate-600">{t('cartTotal')}</p>
                  <p className="text-2xl font-extrabold text-slate-900">CHF {cart.total.toFixed(2)}</p>
                </div>
                <div className="flex gap-3">
                  <Button onClick={() => navigate('/dashboard')} variant="outline" className="flex-1 rounded-full">
                    {t('continueShopping')}
                  </Button>
                  <Button onClick={handleCheckout} disabled={checkingOut}
                    data-testid="checkout-btn"
                    className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full font-bold">
                    {checkingOut ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                      <><CreditCard className="w-4 h-4 mr-2" /> {t('checkout')}</>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Quick links */}
            <div className="text-center">
              <button onClick={() => navigate('/parking')}
                className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1">
                {t('goToParking')} <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
